import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../Firebase';
import Swal from 'sweetalert2';
import './QuestionSystem.css';

// Component receives userName, roomName, and a callback function
const QuestionSystem = ({ userName, onQuestionChange, roomName }) => {
  // State variables to manage the component's behavior
  const [showWaiting, setShowWaiting] = useState(true); // Show "Waiting" message?
  const [currentQuestion, setCurrentQuestion] = useState(null); // Current question number
  const [userAnswer, setUserAnswer] = useState(''); // User's input answer
  const [isChecking, setIsChecking] = useState(false); // Is currently checking database or submitting? (Lock)
  
  // State variables for the "accept" and countdown logic
  const [isAcceptEnabled, setIsAcceptEnabled] = useState(false); // Is the submit button enabled?
  const [answerCountdown, setAnswerCountdown] = useState(10); // Countdown timer value
  const [isCountdownActive, setIsCountdownActive] = useState(false); // Is the countdown running?
  const [acceptCheckInterval, setAcceptCheckInterval] = useState(null); // Interval ID for checking 'accept' field
  const [countdownInterval, setCountdownInterval] = useState(null); // Interval ID for the countdown timer
  const [isShowingAlert, setIsShowingAlert] = useState(false); // Is a SweetAlert currently shown? (Lock)
  
  // Refs to store current values for async operations and prevent stale state issues
  const currentQuestionRef = useRef(null); // Holds the latest question number
  const acceptCheckIntervalRef = useRef(null); // Holds the latest accept check interval ID
  const countdownIntervalRef = useRef(null); // Holds the latest countdown interval ID
  
  // ⬇️ *** [เพิ่ม 1] ***
  // สร้าง Ref สำหรับช่อง Input
  const answerInputRef = useRef(null);

  // --- Functions ---

  /**
   * Checks the 'accept' field in the Admin document.
   * Starts the countdown if 'accept' is true.
   */
  const checkAcceptField = async () => {
    // Guard clauses: Don't check if roomName isn't set, or if busy/already active
    if (!roomName) {
      console.log('Skipping accept check - no roomName');
      return;
    }
    if (isCountdownActive || isChecking || isShowingAlert || isAcceptEnabled) {
      console.log('Skipping accept check - countdown active, checking, showing alert, or accept already enabled');
      // Stop checking permanently if countdown started
      if (isCountdownActive && acceptCheckInterval) {
        clearInterval(acceptCheckInterval);
        setAcceptCheckInterval(null);
      }
      return;
    }
    if (countdownInterval) { // Extra check for safety
      console.log('Skipping accept check - countdown interval exists');
      return;
    }
    if (!currentQuestion && !currentQuestionRef.current) {
      console.log('Skipping accept check - no current question');
      return;
    }
    
    try {
      // Path to the Admin document for the current room
      const adminRef = doc(db, 'Competition-Training', 'nathadaj', roomName, 'Admin');
      const adminSnap = await getDoc(adminRef);
      
      if (adminSnap.exists()) {
        const adminData = adminSnap.data();
        const acceptValue = adminData.accept; // Read the 'accept' field
        
        // If accept is true and we haven't started yet
        if (acceptValue === true && !isAcceptEnabled && !isCountdownActive) {
          console.log('Accept enabled, starting countdown...');
          setIsAcceptEnabled(true); // Enable the submit button
          
          // Clear the interval checking for 'accept'
          if (acceptCheckInterval) {
            clearInterval(acceptCheckInterval);
            setAcceptCheckInterval(null);
          }
          if (acceptCheckIntervalRef.current) { // Also clear ref just in case
             clearInterval(acceptCheckIntervalRef.current);
             acceptCheckIntervalRef.current = null;
          }
          
          startCountdown(); // Start the 8-second timer
        }
      }
    } catch (error) {
      console.error('Error checking accept field:', error);
    }
  };

  /**
   * Starts the 8-second countdown timer.
   */
  const startCountdown = () => {
    // Guard clauses: Prevent multiple countdowns or starting while busy
    if (isCountdownActive || isShowingAlert || countdownIntervalRef.current) {
      console.log('Skipping countdown start - already active, showing alert, or interval ref exists');
      return;
    }
    
    // Immediately stop checking for 'accept' if it was somehow still running
    if (acceptCheckIntervalRef.current) {
      clearInterval(acceptCheckIntervalRef.current);
      acceptCheckIntervalRef.current = null;
      setAcceptCheckInterval(null);
    }
    
    console.log('Starting countdown...');
    setIsCountdownActive(true); // Set state to show countdown UI
    setAnswerCountdown(10); // Reset timer to 10 seconds

    // ⬇️ *** [เพิ่ม 2] ***
    // สั่ง focus ที่ input box ทันที
    if (answerInputRef.current) {
      answerInputRef.current.focus();
    }
    
    // Create the interval
    const newCountdownInterval = setInterval(() => {
      setAnswerCountdown(prev => {
        // Stop immediately if an alert pops up during the countdown
        if (isShowingAlert) {
          console.log('Alert shown during countdown, stopping timer.');
          clearInterval(newCountdownInterval);
          setIsCountdownActive(false);
          if (countdownIntervalRef.current === newCountdownInterval) {
            countdownIntervalRef.current = null;
          }
          setCountdownInterval(null);
          return prev; // Return current value, it won't be used
        }
        
        // Timer reaches zero
        if (prev <= 1) {
          clearInterval(newCountdownInterval); // Stop this interval
          setIsCountdownActive(false); // Update state
          // Clear the ref if this was the active interval
          if (countdownIntervalRef.current === newCountdownInterval) {
            countdownIntervalRef.current = null;
          }
          setCountdownInterval(null); // Clear state interval ID
          handleTimeout(); // Trigger timeout logic
          return 0;
        }
        // Decrement timer
        return prev - 1;
      });
    }, 1000); // Run every second
    
    // Store the interval ID in both state and ref
    countdownIntervalRef.current = newCountdownInterval;
    setCountdownInterval(newCountdownInterval);
  };

  /**
   * Handles the timeout when the countdown finishes.
   * Automatically submits an answer of 0.
   */
  const handleTimeout = () => {
    console.log('Timeout reached. Setting answer to 0 before submitting.');
    // Guard clause: Prevent running if already submitting or showing alert
    if (isChecking || isShowingAlert) {
      console.log('Skipping timeout handling - already checking or showing alert.');
      return;
    }
    
    // Clear any lingering intervals (safety net)
    if (acceptCheckIntervalRef.current) clearInterval(acceptCheckIntervalRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    acceptCheckIntervalRef.current = null;
    countdownIntervalRef.current = null;
    setAcceptCheckInterval(null);
    setCountdownInterval(null);
    setIsCountdownActive(false);
    
    // Set answer state to '0'
    setUserAnswer('0');
    
    // Use setTimeout to ensure state update happens before submitting
    setTimeout(() => {
      console.log('Submitting timeout answer as 0');
      
      // ส่งค่า isTimeout: true ไปโดยตรง
      submitAnswer({ answerValue: 0, isTimeout: true });
    }, 100); // Wait 100ms
  };

  /**
   * Shows a success confirmation SweetAlert after submission.
   */
  const showSuccessAlert = async (isTimeout = false) => {
    // Guard clause: Prevent multiple alerts
    if (isShowingAlert) {
      console.log('Skipping alert display - already showing.');
      return;
    }
    
    console.log('Displaying success alert...');
    setIsShowingAlert(true); // Set lock
    
    // Stop timers immediately when showing alert
    setIsCountdownActive(false);
    setAnswerCountdown(10); // Reset visual countdown
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    if (acceptCheckIntervalRef.current) clearInterval(acceptCheckIntervalRef.current);
    countdownIntervalRef.current = null;
    acceptCheckIntervalRef.current = null;
    setCountdownInterval(null);
    setAcceptCheckInterval(null);
    
    // รอให้ Alert เก่าปิดเสร็จก่อน
    await Swal.close(); 
    
    const alertTitle = isTimeout ? 'หมดเวลา!' : 'ได้ส่งคำตอบเรียบร้อย';
    const alertText = isTimeout ? 'คุณไม่ได้ส่งคำตอบตามกำหนดเวลา' : 'คำตอบของคุณได้รับการบันทึกแล้ว';
    const alertIcon = isTimeout ? 'warning' : 'success';

    Swal.fire({
      title: alertTitle,
      text: alertText,
      icon: alertIcon,
      confirmButtonText: 'ตกลง',
      allowOutsideClick: false, // Prevent closing by clicking outside
      allowEscapeKey: false, // Prevent closing with Esc key
      showConfirmButton: true,
      timer: null, // บังคับให้เป็น null เพื่อให้รอคลิก
    }).then(() => {
      // When the user clicks "OK"
      console.log('SweetAlert closed by user.');
      setIsShowingAlert(false); // Release lock
      resetToWaitingState(); // Go back to the initial "Waiting" state
    }).catch((error) => {
      // Handle potential errors with Swal
      console.error('SweetAlert error:', error);
      setIsShowingAlert(false); // Release lock
      resetToWaitingState(); // Still reset state on error
    });
  };

  /**
   * Resets all state variables back to their initial values,
   * preparing the component to wait for the next question.
   */
  const resetToWaitingState = () => {
    console.log('Resetting to waiting state...');
    
    Swal.close(); // Ensure alerts are closed
    
    // Clear any potential running intervals
    if (acceptCheckIntervalRef.current) clearInterval(acceptCheckIntervalRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    acceptCheckIntervalRef.current = null;
    countdownIntervalRef.current = null;
    
    // Reset all state variables
    setShowWaiting(true);
    setCurrentQuestion(null);
    currentQuestionRef.current = null; // Also reset ref
    setUserAnswer('');
    setIsAcceptEnabled(false);
    setIsCountdownActive(false);
    setAnswerCountdown(10);
    setIsChecking(false);
    setIsShowingAlert(false); // Ensure alert lock is released
    setAcceptCheckInterval(null); // Clear state interval IDs
    setCountdownInterval(null);
    
    console.log('Finished resetting to waiting state.');
  };

  /**
   * Checks the database for a new question item from the Admin.
   * Compares Admin's item number with the User's item number.
   */
  const checkDatabase = async () => {
    // Guard clauses: Don't check if essential props are missing or component is busy
    if (!roomName || !userName) {
      console.log('Skipping database check - no roomName or userName');
      return;
    }
    if (isChecking || isShowingAlert) {
      console.log('Skipping database check - already checking or showing alert');
      return;
    }
    // Don't check if we have a question but haven't started the accept/countdown phase yet
    if (currentQuestion && !isAcceptEnabled && !isCountdownActive) {
      console.log('Skipping database check - current question exists, waiting for accept');
      return;
    }
    
    try {
      setIsChecking(true); // Set lock
      
      // Get Admin and User documents based on roomName and userName
      const adminRef = doc(db, 'Competition-Training', 'nathadaj', roomName, 'Admin');
      const userRef = doc(db, 'Competition-Training', 'nathadaj', roomName, userName);
      
      const [adminSnap, userSnap] = await Promise.all([getDoc(adminRef), getDoc(userRef)]);
      
      if (adminSnap.exists() && userSnap.exists()) {
        const adminData = adminSnap.data();
        const userData = userSnap.data();
        
        const adminItem = adminData.item || 0; // Get Admin's current question number
        const userItem = userData.item || 0;   // Get User's last seen question number
        
        console.log('Admin item:', adminItem, 'User item:', userItem);
        
        // --- New Question Logic ---
        if (adminItem > userItem) {
          console.log('New question found! Refreshing data...');
          
          // Update user's item number in Firestore immediately to "claim" this question
          await setDoc(userRef, { ...userData, item: adminItem }, { merge: true });
          
          // (Optional but good practice) Re-fetch user data to confirm update
          const refreshedUserSnap = await getDoc(userRef);
          if (refreshedUserSnap.exists()) {
            
            // Update Component State to show the new question UI
            setShowWaiting(false);
            setCurrentQuestion(adminItem);
            setUserAnswer(''); // Clear previous answer
            setIsAcceptEnabled(false); // Reset accept state
            setIsChecking(false); // Release lock (will be set again if needed)
            setIsShowingAlert(false); // Ensure alert lock is off
            
            currentQuestionRef.current = adminItem; // Update ref immediately
            
            // Start checking for the 'accept' field now that we have a question
            if (!isAcceptEnabled && !isCountdownActive && !acceptCheckIntervalRef.current) {
              const acceptInterval = setInterval(checkAcceptField, 2000);
              acceptCheckIntervalRef.current = acceptInterval;
              setAcceptCheckInterval(acceptInterval);
            }
            
            // Notify parent component (JitsiMeet) if needed
            if (onQuestionChange) {
              onQuestionChange(adminItem);
            }
          }
          
        // --- Answered Question Logic ---
        } else if (adminItem === userItem && currentQuestion && currentQuestion !== adminItem) {
          // If items match but component still thinks it's on an older question,
          // it means the answer was submitted successfully. Reset to waiting.
          console.log('Question already answered or state mismatch. Resetting to waiting.');
          resetToWaitingState();

        // --- No New Question Logic ---
        } else if (!currentQuestion && !showWaiting) {
           // If somehow we are not showing waiting but have no question, reset
           console.log('No current question but not in waiting state. Resetting.');
           resetToWaitingState();
        }

      } else {
         console.warn("Admin or User document not found for room:", roomName, "user:", userName);
         // Optionally set an error state here if documents should always exist
      }
    } catch (error) {
      console.error('Error checking database:', error);
    } finally {
      setIsChecking(false); // Release lock
    }
  };

  /**
   * Submits the user's answer (or the timeout value) to Firestore.
   * @param {object} { answerValue, isTimeout } - The value to submit and a flag indicating if it was a timeout.
   */
  const submitAnswer = async ({ answerValue, isTimeout = false }) => {
    // Guard clauses: Don't submit if essential props missing or component is busy
    if (!roomName || !userName) {
      console.log('Skipping answer submission - no roomName or userName');
      return;
    }
    if (isChecking || isShowingAlert) {
      console.log('Skipping answer submission - already checking or showing alert');
      return;
    }
    
    // Use the latest question number from state or ref
    const questionNumber = currentQuestion || currentQuestionRef.current;
    
    // Don't submit if there's no question (unless it's a timeout)
    if (!questionNumber && !isTimeout) {
      console.log('Skipping answer submission - no current question');
      return;
    }
    
    setIsChecking(true); // Set lock
    
    // Stop timers immediately on submission attempt
    setIsCountdownActive(false);
    setAnswerCountdown(10);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    if (acceptCheckIntervalRef.current) clearInterval(acceptCheckIntervalRef.current);
    countdownIntervalRef.current = null;
    acceptCheckIntervalRef.current = null;
    setCountdownInterval(null);
    setAcceptCheckInterval(null);
    
    let finalAnswer;
    if (isTimeout) {
      finalAnswer = 0; // Number
    } else {
      // ถ้าเป็นการส่งจากผู้ใช้ (isTimeout = false)
      if (!answerValue || String(answerValue).trim() === '') {
        finalAnswer = 0; // Number
      } else if (isNaN(answerValue)) {
        finalAnswer = 0; // Number
        alert('Invalid input. Saving as 0.');
      } else {
        finalAnswer = Number(answerValue); // แปลงเป็น Number
      }
    }

    try {
      console.log(`Submitting answer for question ${questionNumber || 'timeout'}:`, finalAnswer);
      
      // Get user document reference
      const userRef = doc(db, 'Competition-Training', 'nathadaj', roomName, userName);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        // Create field name dynamically (e.g., 'answer-1', 'answer-2')
        const answerFieldName = questionNumber ? `answer-${questionNumber}` : 'answer-timeout'; // Fallback for safety
        
        // Update the user document with the new answer field
        await setDoc(userRef, { 
          ...userData, 
          [answerFieldName]: finalAnswer // finalAnswer เป็น Number แล้ว
        }, { merge: true }); // Merge to avoid overwriting other fields
        
        console.log(`Answer submitted successfully for question ${questionNumber || 'timeout'}:`, finalAnswer);
        setUserAnswer(''); // Clear input field
        
        // ส่ง isTimeout ที่ได้รับมาโดยตรง
        showSuccessAlert(isTimeout); // Show confirmation
      } else {
        console.error('User document not found. Cannot submit answer.');
        alert('User document not found. Cannot submit answer.');
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      alert('Error submitting answer.');
    } finally {
      setIsChecking(false); // Release lock
    }
  };

  // --- Effects ---

  // Main polling effect: Checks database every 2 seconds when in "Waiting" state
  useEffect(() => {
    let interval;
    // Start interval only if waiting, no question, not busy, and props are ready
    if (showWaiting && !currentQuestion && !isChecking && !isShowingAlert && roomName && userName) {
      interval = setInterval(checkDatabase, 2000);
    }
    // Cleanup function: Clear interval when dependencies change or component unmounts
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [showWaiting, currentQuestion, isChecking, isShowingAlert, roomName, userName]); // Re-run if any of these change

  // Cleanup effect: Ensure intervals are cleared when the component unmounts
  useEffect(() => {
    return () => {
      if (acceptCheckIntervalRef.current) clearInterval(acceptCheckIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, []); // Run only once on unmount

  // --- Render ---
  return (
    <div className="question-system">
      {showWaiting ? (
        // Display waiting message
        <div className="waiting-question">
          <span className="blinking-text">Waiting Question ...</span>
        </div>
      ) : currentQuestion ? (
        // Display question input UI
        <div className="question-section">
          <div className="question-title">
            คำตอบข้อ {currentQuestion}
          </div>
          <div className="answer-input-container">
            <input
              // ⬇️ *** [เพิ่ม 3] ***
              // ผูก Ref เข้ากับ Input
              ref={answerInputRef}
              type="number" // Allow only numbers
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)} // Update state on change
              onKeyDown={(e) => { // Allow Enter key submission if enabled
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (isAcceptEnabled) {
                    // ส่งค่าเป็น object
                    submitAnswer({ answerValue: userAnswer, isTimeout: false });
                  }
                }
              }}
              placeholder="ใส่คำตอบ (ตัวเลขเท่านั้น)"
              className="answer-input"
              disabled={false} // Input is always enabled, button controls submission
            />
            <button 
              // ส่งค่าเป็น object
              onClick={() => submitAnswer({ answerValue: userAnswer, isTimeout: false })}
              className="submit-button"
              disabled={!isAcceptEnabled} // Button enabled only when accept is true
            >
              ส่งคำตอบ
            </button>
            {isCountdownActive && ( // Show countdown timer if active
              <div className="countdown-display">
                <span className="countdown-text">เวลาที่เหลือ: {answerCountdown} วินาที</span>
              </div>
            )}
          </div>
        </div>
      ) : null /* Render nothing if somehow not waiting and no question */}
    </div>
  );
};

export default QuestionSystem;